import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import {
  ImageIcon,
  FileText,
  CalendarClock,
  Instagram,
  Youtube,
  Facebook,
  Twitter,
  Music,
  Mic,
  Download,
  Edit,
  Share2,
  Palette,
} from 'lucide-react';
import CanvaDesignTool from './CanvaDesignTool';

const templateCategories = [
  { id: 'social', name: 'Social Media', icon: <Share2 className="h-4 w-4" /> },
  { id: 'release', name: 'Music Release', icon: <Music className="h-4 w-4" /> },
  { id: 'tour', name: 'Tour & Events', icon: <Mic className="h-4 w-4" /> },
  { id: 'content', name: 'Content Plan', icon: <FileText className="h-4 w-4" /> },
];

const templates = [
  // Social Media Templates
  {
    id: 'social-1',
    category: 'social',
    name: 'Instagram Carousel',
    description: 'A 10-post carousel template for Instagram',
    thumbnail: 'https://placehold.co/600x600/6b21a8/ffffff?text=Instagram+Carousel',
    platform: 'instagram',
  },
  {
    id: 'social-2',
    category: 'social',
    name: 'Twitter Announcement',
    description: 'Template for announcing releases on Twitter',
    thumbnail: 'https://placehold.co/600x300/6b21a8/ffffff?text=Twitter+Announcement',
    platform: 'twitter',
  },
  {
    id: 'social-3',
    category: 'social',
    name: 'Facebook Event',
    description: 'Promote your shows on Facebook',
    thumbnail: 'https://placehold.co/600x315/6b21a8/ffffff?text=Facebook+Event',
    platform: 'facebook',
  },
  {
    id: 'social-4',
    category: 'social',
    name: 'YouTube Thumbnail',
    description: 'Eye-catching thumbnail for music videos',
    thumbnail: 'https://placehold.co/1280x720/6b21a8/ffffff?text=YouTube+Thumbnail',
    platform: 'youtube',
  },
  
  // Music Release Templates
  {
    id: 'release-1',
    category: 'release',
    name: 'Single Release Campaign',
    description: '4-week campaign plan for a single',
    thumbnail: 'https://placehold.co/600x600/6b21a8/ffffff?text=Single+Release',
    platform: 'all',
  },
  {
    id: 'release-2',
    category: 'release',
    name: 'Album Launch',
    description: 'Comprehensive album release strategy',
    thumbnail: 'https://placehold.co/600x600/6b21a8/ffffff?text=Album+Launch',
    platform: 'all',
  },
  
  // Tour Templates
  {
    id: 'tour-1',
    category: 'tour',
    name: 'Tour Announcement',
    description: 'Announce your upcoming tour',
    thumbnail: 'https://placehold.co/600x900/6b21a8/ffffff?text=Tour+Poster',
    platform: 'all',
  },
  {
    id: 'tour-2',
    category: 'tour',
    name: 'Concert Promotion',
    description: 'Promote individual shows',
    thumbnail: 'https://placehold.co/600x600/6b21a8/ffffff?text=Concert+Promo',
    platform: 'all',
  },
  
  // Content Plan Templates
  {
    id: 'content-1',
    category: 'content',
    name: '30-Day Content Calendar',
    description: 'Plan a month of content across platforms',
    thumbnail: 'https://placehold.co/600x400/6b21a8/ffffff?text=Content+Calendar',
    platform: 'all',
  },
  {
    id: 'content-2',
    category: 'content',
    name: 'Weekly Content Planner',
    description: 'Organize your weekly content schedule',
    thumbnail: 'https://placehold.co/600x400/6b21a8/ffffff?text=Weekly+Planner',
    platform: 'all',
  },
];

const platformIcons = {
  instagram: <Instagram className="h-4 w-4" />,
  facebook: <Facebook className="h-4 w-4" />,
  twitter: <Twitter className="h-4 w-4" />,
  youtube: <Youtube className="h-4 w-4" />,
  all: <Share2 className="h-4 w-4" />,
};

const CampaignTemplates: React.FC = () => {
  const { toast } = useToast();
  const [activeCategory, setActiveCategory] = useState('social');
  const [showCanvaDesigner, setShowCanvaDesigner] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  const handleTemplateClick = (templateId: string) => {
    setSelectedTemplate(templateId);
    toast({
      title: "Template selected",
      description: "You can now customize this template or use it as is.",
    });
  };

  const handleUseTemplate = () => {
    toast({
      title: "Using template",
      description: "Template has been added to your campaign.",
    });
  };

  const handleCustomizeWithCanva = () => {
    setShowCanvaDesigner(true);
  };

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold">Campaign Templates</h2>
          <p className="text-muted-foreground">Ready-to-use templates for your marketing campaigns</p>
        </div>
        <Button 
          variant="default" 
          onClick={handleCustomizeWithCanva}
          className="gap-2 bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700"
        >
          <Palette className="h-4 w-4" />
          Create Custom Template
        </Button>
      </div>

      {showCanvaDesigner ? (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold">Design Your Template</h3>
            <Button variant="outline" onClick={() => setShowCanvaDesigner(false)}>
              Back to Templates
            </Button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card className="h-full bg-background/70 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle>Custom Template Design</CardTitle>
                  <CardDescription>
                    Create your own marketing template using Canva
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <CanvaDesignTool 
                    onDesignSave={(url) => {
                      toast({
                        title: "Custom template created",
                        description: "Your template is now ready to use in your campaigns",
                      });
                      setShowCanvaDesigner(false);
                    }}
                  />
                </CardContent>
              </Card>
            </div>
            <div>
              <Card className="bg-background/70 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle>Design Tips</CardTitle>
                  <CardDescription>
                    Best practices for effective marketing templates
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px] pr-4">
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-sm font-medium">Brand Consistency</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Use consistent colors, fonts, and visual elements that align with your artist brand.
                        </p>
                      </div>
                      
                      <Separator />
                      
                      <div>
                        <h3 className="text-sm font-medium">Platform Optimization</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Each platform has ideal dimensions and formats. Design with these specifications in mind.
                        </p>
                        <ul className="text-sm text-muted-foreground mt-2 list-disc pl-5 space-y-1">
                          <li>Instagram: 1080×1080px (square), 1080×1350px (portrait)</li>
                          <li>Twitter: 1200×675px</li>
                          <li>Facebook: 1200×630px</li>
                          <li>YouTube Thumbnail: 1280×720px</li>
                        </ul>
                      </div>
                      
                      <Separator />
                      
                      <div>
                        <h3 className="text-sm font-medium">Visual Hierarchy</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Guide viewers' attention by emphasizing the most important elements first.
                        </p>
                      </div>
                      
                      <Separator />
                      
                      <div>
                        <h3 className="text-sm font-medium">Text Readability</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Keep text minimal. Use readable fonts with good contrast against backgrounds.
                        </p>
                      </div>
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      ) : (
        <Tabs value={activeCategory} onValueChange={setActiveCategory}>
          <TabsList className="mb-6 bg-muted/50">
            {templateCategories.map(category => (
              <TabsTrigger key={category.id} value={category.id} className="data-[state=active]:bg-background">
                <div className="flex items-center gap-2">
                  {category.icon}
                  <span>{category.name}</span>
                </div>
              </TabsTrigger>
            ))}
          </TabsList>
          
          {templateCategories.map(category => (
            <TabsContent key={category.id} value={category.id} className="mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates
                  .filter(template => template.category === category.id)
                  .map(template => (
                    <Card 
                      key={template.id} 
                      className={`overflow-hidden transition-all hover:shadow-lg ${
                        selectedTemplate === template.id ? 'ring-2 ring-purple-500' : ''
                      }`}
                      onClick={() => handleTemplateClick(template.id)}
                    >
                      <div className="aspect-w-16 aspect-h-9 bg-muted">
                        <img 
                          src={template.thumbnail} 
                          alt={template.name} 
                          className="object-cover w-full h-full"
                        />
                      </div>
                      <CardHeader className="p-4">
                        <div className="flex justify-between items-start">
                          <CardTitle className="text-lg">{template.name}</CardTitle>
                          <div className="p-1 rounded-md bg-muted">
                            {platformIcons[template.platform as keyof typeof platformIcons]}
                          </div>
                        </div>
                        <CardDescription>{template.description}</CardDescription>
                      </CardHeader>
                      <CardFooter className="flex justify-between p-4 pt-0">
                        <Button variant="outline" size="sm" className="gap-1" onClick={handleUseTemplate}>
                          <Download className="h-3 w-3" />
                          Use
                        </Button>
                        <Button variant="outline" size="sm" className="gap-1" onClick={handleCustomizeWithCanva}>
                          <Edit className="h-3 w-3" />
                          Customize
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
};

export default CampaignTemplates;
